import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { PackagePlus, ClipboardList, SlidersHorizontal, ArrowRightLeft, Eye } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return dateStr; }
};

const DocRow = ({ doc, onClick, testId, children }) => (
  <div
    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer border-b last:border-0"
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </div>
);

export const DocumentsTab = ({ documents, onViewDetail }) => {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* Receipts */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <PackagePlus className="w-4 h-4 text-green-600" />
            Receptii ({documents?.receipts?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-80 overflow-y-auto">
          {documents?.receipts?.map((doc, idx) => (
            <DocRow key={idx} onClick={() => onViewDetail('receipt', doc.id)} testId={`doc-receipt-${doc.id}`}>
              <div>
                <p className="font-medium text-sm">{doc.number}</p>
                <p className="text-xs text-slate-500">{doc.supplier_name} • {formatDate(doc.date)?.split(',')[0]}</p>
              </div>
              <div className="text-right flex items-center gap-2">
                <p className="font-semibold text-green-600">+{doc.quantity}</p>
                <Eye className="w-4 h-4 text-slate-400" />
              </div>
            </DocRow>
          ))}
          {documents?.receipts?.length === 0 && <p className="text-center text-slate-400 py-4 text-sm">Nicio receptie</p>}
        </CardContent>
      </Card>

      {/* Tickets */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-red-600" />
            Tichete Consum ({documents?.tickets?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-80 overflow-y-auto">
          {documents?.tickets?.map((doc, idx) => (
            <DocRow key={idx} onClick={() => onViewDetail('ticket', doc.id)} testId={`doc-ticket-${doc.id}`}>
              <div>
                <p className="font-medium text-sm">{doc.number}</p>
                <p className="text-xs text-slate-500 truncate max-w-48">{doc.title}</p>
              </div>
              <div className="text-right flex items-center gap-2">
                <p className="font-semibold text-red-600">-{doc.quantity}</p>
                <Eye className="w-4 h-4 text-slate-400" />
              </div>
            </DocRow>
          ))}
          {documents?.tickets?.length === 0 && <p className="text-center text-slate-400 py-4 text-sm">Niciun tichet</p>}
        </CardContent>
      </Card>

      {/* Adjustments */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-amber-600" />
            Ajustari ({documents?.adjustments?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-80 overflow-y-auto">
          {documents?.adjustments?.map((doc, idx) => (
            <DocRow key={idx} onClick={() => onViewDetail('adjustment', doc.id)} testId={`doc-adjustment-${doc.id}`}>
              <div>
                <p className="font-medium text-sm">{doc.reason || doc.type}</p>
                <p className="text-xs text-slate-500">{doc.operator} • {formatDate(doc.date)?.split(',')[0]}</p>
              </div>
              <div className="text-right flex items-center gap-2">
                <p className={`font-semibold ${doc.diff > 0 ? 'text-green-600' : doc.diff < 0 ? 'text-red-600' : ''}`}>
                  {doc.diff > 0 ? '+' : ''}{doc.diff}
                </p>
                <Eye className="w-4 h-4 text-slate-400" />
              </div>
            </DocRow>
          ))}
          {documents?.adjustments?.length === 0 && <p className="text-center text-slate-400 py-4 text-sm">Nicio ajustare</p>}
        </CardContent>
      </Card>

      {/* Transfers */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-blue-600" />
            Transferuri ({documents?.transfers?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-80 overflow-y-auto">
          {documents?.transfers?.map((doc, idx) => (
            <DocRow key={idx} onClick={() => onViewDetail('transfer', doc.id)} testId={`doc-transfer-${doc.id}`}>
              <div>
                <p className="font-medium text-sm">{doc.from_bin} → {doc.to_bin}</p>
                <p className="text-xs text-slate-500">{doc.operator} • {formatDate(doc.date)?.split(',')[0]}</p>
              </div>
              <div className="text-right flex items-center gap-2">
                <p className="font-semibold text-blue-600">{doc.quantity}</p>
                <Eye className="w-4 h-4 text-slate-400" />
              </div>
            </DocRow>
          ))}
          {documents?.transfers?.length === 0 && <p className="text-center text-slate-400 py-4 text-sm">Niciun transfer</p>}
        </CardContent>
      </Card>
    </div>
  );
};
